import {
  ProductDoc,
  ProductInventoryDoc,
  ProductInventoryUnit,
  ProductSupplierDoc,
  PurchaseBundleType,
} from '@gooduncles/gu-app-schema';
import { Button, Card, Descriptions, Divider, Form, Input, InputNumber, Select, Space, message } from 'antd';
import { FC, useCallback, useEffect, useState } from 'react';
import { purchaseBundleToKr, purchaseConfigTooltipMap } from 'src/constants';
import { updateProductInventory } from 'src/utils/firebase/firebase-short-cut';
import { getPurchasePreview } from 'src/utils/product-inventory-util';

import { ProductInventoryWithDetail } from 'src/hooks/subscription/useProductInventoriesWithDetail';

import ProductInfo from './ProductInfo';

interface InventoryDetailProps {
  inventory: ProductInventoryDoc;
  unit: ProductInventoryUnit;
  product?: ProductDoc;
  inventories?: ProductInventoryWithDetail[];
  productSuppliers: ProductSupplierDoc[];
}

const InventoryDetail: FC<InventoryDetailProps> = ({
  inventory,
  unit,
  product,
  inventories = [],
  productSuppliers,
}) => {
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedConfig, setEditedConfig] = useState(inventory.purchaseConfig);
  const ratio = inventory.uom[unit]?.ratio ?? 1;

  const handleUpdatePurchaseConfig = useCallback(async () => {
    setLoading(true);
    try {
      await updateProductInventory(inventory._id, { purchaseConfig: editedConfig });
      message.success('발주 설정을 수정했습니다.');
    } catch (error) {
      message.error('발주 설정 수정에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, [editedConfig, inventory._id]);

  useEffect(() => {
    setEditedConfig(inventory.purchaseConfig);
  }, [inventory.purchaseConfig]);

  return (
    <div className='tabBody'>
      <Space direction='vertical' size='middle' style={{ width: '100%' }}>
        <ProductInfo
          inventory={inventory}
          unit={unit}
          product={product}
          inventories={inventories}
          productSuppliers={productSuppliers}
        />

        <Divider style={{ margin: '8px 0' }} />

        <Descriptions title='발주 미리보기(수량 10개 예시)' column={1}>
          <Descriptions.Item label='발주내용'>
            {getPurchasePreview(inventory.purchaseConfig, ratio * 10)}
          </Descriptions.Item>
        </Descriptions>

        <Card
          title='발주 설정'
          extra={
            <Button onClick={() => setIsEditing(!isEditing)} loading={loading}>
              {isEditing ? '취소' : '수정'}
            </Button>
          }>
          {isEditing ? (
            <Form layout='vertical'>
              <Form.Item
                label='발주품명'
                tooltip={{
                  title: purchaseConfigTooltipMap.purchaseProductName,
                  overlayStyle: { whiteSpace: 'pre-line' },
                }}>
                <Input
                  value={editedConfig.purchaseProductName || ''}
                  onChange={(e) =>
                    setEditedConfig({
                      ...editedConfig,
                      purchaseProductName: e.target.value,
                    })
                  }
                />
              </Form.Item>
              <Form.Item
                label='묶음방식'
                tooltip={{
                  title: purchaseConfigTooltipMap.purchaseBundle,
                  overlayStyle: { whiteSpace: 'pre-line' },
                }}>
                <Select
                  value={editedConfig.purchaseBundle}
                  onChange={(value) =>
                    setEditedConfig({
                      ...editedConfig,
                      purchaseBundle: value,
                    })
                  }>
                  {Object.values(PurchaseBundleType).map((type) => (
                    <Select.Option key={type} value={type}>
                      {purchaseBundleToKr[type as keyof typeof purchaseBundleToKr]}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label='단위 변환 값'
                tooltip={{
                  title: purchaseConfigTooltipMap.purchaseMultiplyUnit,
                  overlayStyle: { whiteSpace: 'pre-line' },
                }}>
                <InputNumber
                  style={{ width: '100%' }}
                  value={editedConfig.purchaseMultiplyUnit}
                  onChange={(value) =>
                    setEditedConfig({
                      ...editedConfig,
                      purchaseMultiplyUnit: value || 0,
                    })
                  }
                />
              </Form.Item>
              <Form.Item
                label='최대 묶음'
                tooltip={{
                  title: purchaseConfigTooltipMap.purchaseMaxBundle,
                  overlayStyle: { whiteSpace: 'pre-line' },
                }}>
                <InputNumber
                  style={{ width: '100%' }}
                  value={editedConfig.purchaseMaxBundle || undefined}
                  onChange={(value) =>
                    setEditedConfig({
                      ...editedConfig,
                      purchaseMaxBundle: value || null,
                    })
                  }
                />
              </Form.Item>
              <Form.Item
                label='단위'
                tooltip={{
                  title: purchaseConfigTooltipMap.purchaseUnit,
                  overlayStyle: { whiteSpace: 'pre-line' },
                }}>
                <Input
                  value={editedConfig.purchaseUnit || ''}
                  onChange={(e) =>
                    setEditedConfig({
                      ...editedConfig,
                      purchaseUnit: e.target.value,
                    })
                  }
                />
              </Form.Item>
              <Form.Item>
                <Button type='primary' block onClick={handleUpdatePurchaseConfig} loading={loading}>
                  저장
                </Button>
              </Form.Item>
            </Form>
          ) : (
            <Descriptions column={1}>
              <Descriptions.Item label='발주 상품명'>
                {inventory.purchaseConfig.purchaseProductName || '-'}
              </Descriptions.Item>
              <Descriptions.Item label='묶음방식'>
                {purchaseBundleToKr[inventory.purchaseConfig.purchaseBundle] || '-'}
              </Descriptions.Item>
              <Descriptions.Item label='최대 묶음'>
                {inventory.purchaseConfig.purchaseMaxBundle || '-'}
              </Descriptions.Item>
              <Descriptions.Item label='단위 변환 값'>
                {inventory.purchaseConfig.purchaseMultiplyUnit}
              </Descriptions.Item>
              <Descriptions.Item label='단위'>{inventory.purchaseConfig.purchaseUnit || '-'}</Descriptions.Item>
            </Descriptions>
          )}
        </Card>
      </Space>
    </div>
  );
};

export default InventoryDetail;
